import { useMemo } from 'react'

import { Icon, Chip } from '@circlefin/components'
import { createColumnHelper } from '@circlefin/components/lib/AdvancedTable'
import { useDate, useMoney } from '@circlefin/hooks'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import type { Locale } from '@circlefin/money/variants'
import type { Amount, Invoice } from '@shared/graphql'

const columnHelper = createColumnHelper<Invoice>()

export const useInvoiceTableColumn = () => {
  const { t } = useTranslation('settings')
  const { locale } = useRouter()
  const { money } = useMoney({ locale: locale as Locale })
  const { date } = useDate({ locale: locale as Locale })

  const textClassNames = 'text-sm leading-5 text-black-600'

  return useMemo(() => {
    const getAmount = (value: Amount) => (
      <span className={classNames(textClassNames, 'font-circular-regular')}>
        {money({
          number: value.amount,
          variant: value.currency,
          options: {
            signDisplay: false,
            symbol: true,
          },
        })}
      </span>
    )

    /**
     * Invoice Number column.
     */
    const invoiceNumber = columnHelper.accessor('invoiceNumber', {
      header: t<string>`settings:billing.invoices.columns.invoiceNumber`,
      cell: ({ getValue }) => (
        <div className="flex items-center">
          <Icon
            className="mr-3 text-black-500"
            name="DocumentTextOutline"
            size={12}
          />
          <span className={classNames(textClassNames, 'font-circular-book')}>
            {getValue()}
          </span>
        </div>
      ),
    })

    /**
     * Issue Date column.
     */
    const issueDate = columnHelper.accessor('invoiceDate', {
      header: t<string>`settings:billing.invoices.columns.issueDate`,
      cell: ({ getValue }) => (
        <span className={classNames(textClassNames, 'font-circular-regular')}>
          {date(getValue(), 'date')}
        </span>
      ),
    })

    /**
     * Amount Total.
     */
    const amountTotal = columnHelper.accessor('amountTotal', {
      header: t<string>`settings:billing.invoices.columns.amountTotal`,
      cell: ({ getValue }) => getAmount(getValue()),
    })

    /**
     * Amount Due column.
     */
    const amountDue = columnHelper.accessor('amountDue', {
      header: t<string>`settings:billing.invoices.columns.amountDue`,
      cell: ({ getValue }) => getAmount(getValue()),
    })

    /**
     * Status column.
     */
    const status = columnHelper.accessor('status', {
      header: t<string>`common:status`,
      cell: ({ getValue }) => {
        const value = getValue()

        return (
          <div className="flex items-center justify-between">
            <Chip
              variant={
                value === 'payment_due' ? 'status/error' : 'status/neutral'
              }
            >
              {t(`settings:billing.invoices.status.${value}`)}
            </Chip>
            <Icon
              className="ml-4 text-black-500"
              name="ChevronRightSolid"
              size={20}
            />
          </div>
        )
      },
    })

    return [invoiceNumber, issueDate, amountTotal, amountDue, status]
  }, [date, money, t])
}
